import type { ILandingCustomModuleNavRef } from './landingButtonCustomModule'
import type { ILandingTemplateButton } from './landingTemplateButtons'
import type { ProjectRouteIdentity } from '~/shared/lib'
import { resolveEventModuleRoute } from '~/entities/round-table/shared/resolveEventModuleRoute'
import { resolveModuleKindBySlug } from '~/entities/round-table/shared/resolveModuleKindBySlug'
import { CustomModuleBaseTypeEnum } from '~/shared/api'
import { ProjectModuleSlugEnum } from '~/shared/const'
import { isMySpeechSlug } from '~/shared/const/workspaceModuleCreate'
import { getProjectRouteParam, permissions } from '~/shared/lib'
import { resolveLandingCustomModuleFromButton } from './landingButtonCustomModule'
import { resolveLandingModuleObjectDetailRoute } from './landingModuleObjectSelect'
import { normalizeWorkspaceModuleSlug } from './workspaceModuleVisibility'

export function navigateToLandingModuleButton(
  project: ProjectRouteIdentity | string,
  button: Pick<ILandingTemplateButton, 'moduleSlug' | 'moduleObjectId' | 'moduleType'>,
) {
  const routeParam = typeof project === 'string'
    ? project
    : getProjectRouteParam(project)

  if (button.moduleObjectId?.trim()) {
    const detailRoute = resolveLandingModuleObjectDetailRoute(project, button)

    if (detailRoute) {
      void navigateTo({
        name: detailRoute.name,
        params: detailRoute.params,
        query: detailRoute.query,
      })
      return
    }
  }

  const slug = normalizeWorkspaceModuleSlug(button.moduleSlug)
  const query = { moduleSlug: slug }

  if (isMySpeechSlug(slug)) {
    void navigateTo({
      name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.PERMISSION,
      params: { id: routeParam },
      query,
    })
    return
  }

  switch (slug) {
    case ProjectModuleSlugEnum.AboutProject:
      void navigateTo({
        name: permissions.PROJECTS.PROJECT.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.ProjectMembers:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.MEMBERS.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.ProjectManagement:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.MANAGEMENTS.SETTINGS.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.Supporters:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.SUPPORTERS.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.ProjectNews:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.NEWS.PERMISSION,
        params: { id: routeParam },
        query,
      })
      break
    case ProjectModuleSlugEnum.ProjectSurvey:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.SURVEYS.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.ProjectOrgMail:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.MAIL.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.ProjectSpeeches:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.PERMISSION,
        params: { id: routeParam },
        query,
      })
      break
    case 'question_to_deputy':
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.QUESTION_TO_DEPUTY.PERMISSION,
        params: { id: routeParam },
      })
      break
    case 'service_phones':
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.EMERGENCY_CONTACTS.PERMISSION,
        params: { id: routeParam },
      })
      break
    case 'project_counters':
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.COUNTERS.PERMISSION,
        params: { id: routeParam },
      })
      break
    case 'consumer_rights':
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.RIGHTS_OF_CONSUMER.PERMISSION,
        params: { id: routeParam },
      })
      break
    case 'accident_guidance':
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.WHAT_TO_DO_IN_CASE_OF_ACCIDENT.PERMISSION,
        params: { id: routeParam },
      })
      break
    case ProjectModuleSlugEnum.ProjectCalendar:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.CALENDAR.PERMISSION,
        params: { id: routeParam },
        query: { moduleSlug: slug },
      })
      break
    default: {
      const customModule = resolveLandingCustomModuleFromButton(button)
      if (customModule) {
        navigateToCustomModule(routeParam, customModule)
        break
      }

      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.PERMISSION,
        params: { id: routeParam },
      })
    }
  }
}

function navigateToCustomModule(
  routeParam: string,
  mod: ILandingCustomModuleNavRef,
) {
  const query = { moduleSlug: mod.moduleSlug }

  if (isMySpeechSlug(mod.moduleSlug)) {
    void navigateTo({
      name: permissions.PROJECTS.WORKSPACE.MY_SPEECH.PERMISSION,
      params: { id: routeParam },
      query,
    })
    return
  }

  switch (mod.moduleType) {
    case CustomModuleBaseTypeEnum.News:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.NEWS.PERMISSION,
        params: { id: routeParam },
        query,
      })
      break
    case CustomModuleBaseTypeEnum.Survey:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.SURVEYS.PERMISSION,
        params: { id: routeParam },
        query,
      })
      break
    case CustomModuleBaseTypeEnum.Supporters:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.SUPPORTERS.PERMISSION,
        params: { id: routeParam },
        query,
      })
      break
    case CustomModuleBaseTypeEnum.Mail:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.MAIL.PERMISSION,
        params: { id: routeParam },
        query,
      })
      break
    case CustomModuleBaseTypeEnum.ObjectPassport: {
      const kind = resolveModuleKindBySlug(mod.moduleSlug)
      if (kind) {
        void navigateTo({
          ...resolveEventModuleRoute(kind, routeParam, mod.moduleSlug, 'entry'),
        })
      }
      break
    }
    default:
      void navigateTo({
        name: permissions.PROJECTS.WORKSPACE.PERMISSION,
        params: { id: routeParam },
        query,
      })
  }
}
